import { InfoCircleFilled } from "@ant-design/icons";
import { Button, Form, Modal } from "antd";
import { useContext, useMemo, useState } from "react";
import { message } from "utils/ciq-message";
import dayjs from "utils/dayjs-config";
import DisplayNameListPopover from "components/bulk-edit-material/display-name-list";
import CIQDatePicker from "components/custom-date-picker";
import { getFeatureDetail } from "components/feature-entity-linking-page/utils";
import { SidepanelStyles } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import {
  linkBidPackages,
  TOnsiteEstimateUpdateSummary
} from "services/bid-package-links";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";
import { TBidPackageLog } from "../../model";
import {
  isBidPackageCompleted,
  isOnsiteEstimateEditable
} from "../../onsite-estimate";

type TBidPackageListItem = { id: string; name: string };

const toBidPackageListItem = (
  bidPackage: TBidPackageLog
): TBidPackageListItem => ({
  id: bidPackage.id,
  name: `${bidPackage.auto_inc_value ?? ""} - ${bidPackage.title ?? ""}`
});

/** Splits the selection the same way the server does for `skip_completed`. */
export function partitionOnsiteEstimateRows(bidPackages: TBidPackageLog[]) {
  const eligible: TBidPackageLog[] = [];
  const completed: TBidPackageLog[] = [];
  const notApplicable: TBidPackageLog[] = [];
  bidPackages.forEach((bidPackage) => {
    if (isBidPackageCompleted(bidPackage)) completed.push(bidPackage);
    else if (!isOnsiteEstimateEditable(bidPackage))
      notApplicable.push(bidPackage);
    else eligible.push(bidPackage);
  });
  return { eligible, completed, notApplicable };
}

/** The onsite estimate shared by all bid packages, or null when they differ. */
function commonOnsiteEstimate(bidPackages: TBidPackageLog[]) {
  const firstOnsiteEstimate = bidPackages[0]?.onsite_estimate_date ?? null;
  const allShareOnsiteEstimate = bidPackages.every(
    (bidPackage) =>
      (bidPackage.onsite_estimate_date ?? null) === firstOnsiteEstimate
  );
  return allShareOnsiteEstimate && firstOnsiteEstimate
    ? DateUtils.dateTimeObj(firstOnsiteEstimate)
    : null;
}

function SkippedBanner({
  skippedBidPackages,
  singular,
  plural,
  reason,
  testId
}: Readonly<{
  skippedBidPackages: TBidPackageListItem[];
  singular: string;
  plural: string;
  reason: string;
  testId: string;
}>) {
  if (skippedBidPackages.length === 0) return null;
  return (
    <div className="pb-3" data-testid={testId}>
      <div className="flex items-center space-x-2 text-xs px-3 py-2 border border-solid border-ciq-blue-300 bg-ciq-blue-100 rounded">
        <InfoCircleFilled className="text-ciq-blue-700" />
        <div className="text-black text-opacity-80">
          <DisplayNameListPopover list={skippedBidPackages} placement="left">
            {skippedBidPackages.length}
          </DisplayNameListPopover>{" "}
          {skippedBidPackages.length === 1 ? `${singular} is` : `${plural} are`}{" "}
          {reason} and will not be updated.
        </div>
      </div>
    </div>
  );
}

function BulkEditOnsiteEstimateModal({
  selectedRows,
  label,
  deadlineLabel,
  onCancel,
  onApplied
}: Readonly<{
  selectedRows: TBidPackageLog[];
  label: string;
  deadlineLabel: string;
  onCancel: () => void;
  onApplied: () => void;
}>) {
  const { tokenRetrievalState } = useContext(ProjectContext);
  const featureDetail = getFeatureDetail(FeatureTypes.BID_PACKAGE);

  const { eligible, completed, notApplicable } = useMemo(
    () => partitionOnsiteEstimateRows(selectedRows),
    [selectedRows]
  );

  const [onsiteEstimateDate, setOnsiteEstimateDate] =
    useState<dayjs.Dayjs | null>(() => commonOnsiteEstimate(eligible));
  const [isDiff, setIsDiff] = useState(false);
  const [inProgress, setInProgress] = useState(false);

  const onOnsiteEstimateChange = (selectedDate: dayjs.Dayjs | null) => {
    setIsDiff(true);
    setOnsiteEstimateDate(selectedDate);
  };

  const onApply = async () => {
    if (eligible.length === 0) return;
    setInProgress(true);
    try {
      const response = await linkBidPackages(
        {
          bid_package_ids: eligible.map((bidPackage) => bidPackage.id),
          planning_only_activity_date: onsiteEstimateDate
            ? DateUtils.formatDateWithLunchTime(onsiteEstimateDate)
            : null,
          skip_completed: true
        },
        tokenRetrievalState?.token ?? ""
      );
      if (!response.success) {
        message.error(response.errorMsg || "Failed to update.");
        return;
      }

      const summary = response.data as
        | Partial<TOnsiteEstimateUpdateSummary>
        | undefined;
      const updatedCount = summary?.updated_ids?.length ?? eligible.length;
      const skippedCount =
        (summary?.skipped_completed_ids?.length ?? 0) +
        (summary?.skipped_not_applicable_ids?.length ?? 0);
      const bidPackageNoun = (count: number) =>
        count === 1 ? featureDetail.title : featureDetail.titlePlural;

      if (skippedCount > 0) {
        message.warning(
          `Updated ${updatedCount} ${bidPackageNoun(updatedCount)}. ${skippedCount} ${bidPackageNoun(
            skippedCount
          )} could not be updated.`
        );
      } else {
        message.success(
          `Updated ${updatedCount} ${bidPackageNoun(updatedCount)}.`
        );
      }
      onApplied();
    } catch (error) {
      console.error("Bulk onsite estimate update failed:", error);
      message.error("Failed to update.");
    } finally {
      setInProgress(false);
    }
  };

  return (
    <Modal
      title={
        <div data-testid="bulk-edit-onsite-estimate-title">
          <span>Bulk Edit {label}</span>
          <span className="text-xs font-medium ml-2 mr-2 text-black text-opacity-60">
            |
          </span>
          <span className="text-xs font-medium text-black text-opacity-60">
            {selectedRows.length} Selected {featureDetail.titlePlural}
          </span>
        </div>
      }
      className="custom-drawer"
      style={SidepanelStyles.style}
      width={420}
      open
      onCancel={onCancel}
      destroyOnHidden
      data-testid="bulk-edit-onsite-estimate-modal"
      footer={[
        <div className="flex w-full space-x-3 justify-end" key="footer">
          <Button
            onClick={onCancel}
            disabled={inProgress}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            disabled={!isDiff || inProgress || eligible.length === 0}
            loading={inProgress}
            onClick={onApply}
            data-testid="apply-button"
          >
            Apply
          </Button>
        </div>
      ]}
    >
      <SkippedBanner
        skippedBidPackages={completed.map(toBidPackageListItem)}
        singular={featureDetail.title}
        plural={featureDetail.titlePlural}
        reason="completed"
        testId="bulk-edit-onsite-estimate-completed-banner"
      />
      <SkippedBanner
        skippedBidPackages={notApplicable.map(toBidPackageListItem)}
        singular={featureDetail.title}
        plural={featureDetail.titlePlural}
        reason="planned through linked Materials or Submittals"
        testId="bulk-edit-onsite-estimate-not-applicable-banner"
      />

      <div className="text-black text-opacity-90 font-semibold">
        Set the {label} for the {featureDetail.titlePlural}
      </div>
      <div
        className="text-black text-opacity-50 text-xs"
        data-testid="bulk-edit-onsite-estimate-helper"
      >
        {deadlineLabel} will be recalculated from the {label} using each{" "}
        {featureDetail.title}&apos;s existing Submittal and Material Plan
        durations, or the default durations where none exist.
      </div>
      <Form
        layout="vertical"
        className="space-y-3 min-h-full pt-3"
        disabled={inProgress || eligible.length === 0}
        data-testid="bulk-edit-onsite-estimate-form"
      >
        <div className="flex-col overflow-y overflow-x-hidden h-full px-1">
          <Form.Item label={label}>
            <CIQDatePicker
              className="w-full"
              format={DATE_FORMAT_MMDDYYYY}
              placeholder={`Select ${label}`}
              placement="topLeft"
              allowClear={false}
              value={onsiteEstimateDate}
              onChange={onOnsiteEstimateChange}
              data-testid="bulk-edit-onsite-estimate-date-picker"
              renderExtraFooter={() =>
                onsiteEstimateDate ? (
                  <div className="flex justify-center py-2 w-full">
                    <Button
                      onClick={() => onOnsiteEstimateChange(null)}
                      data-testid="bulk-edit-onsite-estimate-clear-date"
                    >
                      Clear Date
                    </Button>
                  </div>
                ) : null
              }
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}

export default BulkEditOnsiteEstimateModal;
